import React, { Suspense, lazy, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AddIcon from '@mui/icons-material/Add'
import { Visibility, FileDownloadOutlined, DeleteOutline } from '@mui/icons-material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../../../generic/hooks/useAuthorization'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { apiDelete, apiDownloadFile } from '../../../../generic/Api_Functions'
import { SmartDialog, SmartDeleteDialog } from '../../../../generic/utilities/SmartDialog'
import { raisedColor, sunkenColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'
import CollapsedFieldsLine from './CollapsedFieldsLine'
import { EMPTY_DASH, ColumnHeader } from '../detailShared'

// Both of these reach `three` (SmartDocumentViewer -> SmartThreejs -> three/examples/.../OrbitControls),
// and neither is needed until the user asks for a document: attaching opens a dialog, viewing opens
// another. Imported statically, opening the detail on ANY block paid for a 3D engine nobody had asked
// for. Loaded on demand, the chunk only travels when one of the two dialogs opens.
const Document = lazy(() => import('../../../document/Document'))
// `SmartDocumentViewer` is a named export from the import-free leaf module (the `documentExtensions.js`
// code-splitting invariant); `lazy` wants a default, hence the remap.
const SmartDocumentViewer = lazy(() =>
  import('../../../document/SmartDocumentViewer').then((module) => ({ default: module.SmartDocumentViewer }))
)

const DocumentDialogFallback = () => (
  <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 160 }}>
    <CircularProgress size={28} />
  </Stack>
)

// RN8 (§2.2) — la ausencia se DECLARA. Ningún dato del documento se queda en blanco: el contrato N6
// manda `documentType`, `createdUser` y `createdDate` como `string | null`, y una celda vacía obliga
// al lector a adivinar si falta el dato o falta el fichero.

// ⚠ Decisión del PO (2026-09-15): los dos últimos rótulos se CRUZAN, no se renombran sueltos. El
// nombre que deja libre `ORDER` lo ocupa `EXECUTION`, así que hacerlo a medias dejaría dos grupos
// llamándose igual. Los identificadores de procedencia (`ORDER`, `EXECUTION`) NO cambian: son el
// enum de Sun. Lo que cambia es lo que se lee.
//   ORDER      -> documentos de la ORDEN DE FABRICACIÓN ("FO documents" — `FO` y no `OF`: la clave
//                 es el texto INGLÉS, y `OF` es la abreviatura castellana, que en inglés no dice
//                 nada. En español el rótulo sigue siendo "Documentos de OF".)
//   EXECUTION  -> documentos del PEDIDO del que cuelga ("Order documents")
// Y por eso las traducciones de `Order documents` había que revisarlas una a una: cuatro idiomas
// (ca, eu, fr, pt) decían "ordre/ordena/ordem", que en este dominio es la OF y no el pedido.
//
// 📌 Este mapa TRADUCE, no decide: qué grupos hay y en qué orden se leen lo dicen los datos que
// llegan (`documents.groups`), no una lista escrita a mano. Una procedencia que Sun añada mañana se
// pinta igual, con su identificador por rótulo, en vez de desaparecer de la vista.
// ⚠️⚠️ ESTOS DOS ESTABAN CRUZADOS, y llevaban así desde que se fijaron: `ORDER` se rotulaba
// «documentos de OF» y `EXECUTION` «documentos de pedido», que es justo al revés. Cotejado con la
// agrupación de Sun (auditoría E49): `EXECUTION` recoge el tipo `FABRICATION_ORDER` —los de la
// propia orden, y por eso es el ÚNICO grupo con `attachable`—, mientras que `ORDER` recoge `ORDER`
// y `ORDER_LINE`, que son los del pedido comercial y su línea.
//
// 📌 CÓMO SE DESTAPÓ, porque la forma se repite: el rótulo llevaba años mirándose sin que nadie
// notara nada. Lo cantó ponerlo AL LADO DE UNA ACCIÓN QUE LO CONTRADECÍA — al marcar el único
// grupo donde se puede adjuntar, la sección con el botón «Adjuntar documento» pasó a decir que era
// del pedido comercial. Un rótulo suelto no se puede desmentir; junto a lo que hace, sí.
//
// ⚠️ Y sus tests FIJABAN EL VALOR EQUIVOCADO, que es exactamente por lo que sobrevivió: un test que
// copia lo que el código hace hoy no protege nada, lo conserva.
const GROUP_TITLE_KEYS = {
  PART_REFERENCE: 'Part reference documents',
  PROCESS: 'Process documents',
  ORDER: 'Order documents',
  EXECUTION: 'FO documents'
}

const documentsOf = (group) => group.documents || []

// Contrato N6: «`attachable` es `true` solo en `EXECUTION`, que es el ámbito de la propia orden. En
// los otros tres grupos llega `false` siempre».
const DECLARED_ATTACHABLE_PROVENANCE = 'EXECUTION'

// Qué grupo admite adjuntar y retirar es una CAPACIDAD, y la capacidad la dice el dato: `attachable`
// (RN45/D10), el mismo campo que ya decide si una fila ofrece el botón de retirar. Se busca primero
// en los documentos que llegan, para que la marca del bloque y la de las filas no puedan
// contradecirse.
//
// El respaldo declarado hace falta porque el caso que el rediseño quiere resaltar es justo el que
// deja mudo al dato: un grupo de la OF VACÍO —«ninguno adjunto todavía»— no tiene ningún documento
// que lleve `attachable`. Ahí, y solo ahí, se cae a lo que el contrato declara.
export const attachableProvenanceOf = (groups = []) => {
  const carrying = groups.filter((group) => documentsOf(group).some((document) => document.attachable))
  return carrying.length > 0 ? carrying[0].provenance : DECLARED_ATTACHABLE_PROVENANCE
}

// RN46/P-3: a document whose size Sun could not compute travels `sizeBytes: null` — this NEVER
// renders as "0 B" (S213), always the ausencia dash.
export const documentSizeLabel = (sizeBytes) => {
  if (sizeBytes === null || sizeBytes === undefined) {
    return EMPTY_DASH
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

// CU-L5/CU-W5 (T5a) — Documents block: the provenance groups Sun sends (RN44), each document's
// origin/author/date/size (RN46) with open (VIEW)/download (DOWNLOAD)/remove (DELETE, only where
// `attachable` is true, D10) actions, each gated by its OWN permission (RN72/RN45), plus the existing
// documents flow (`Document.jsx`, untouched) reused as-is for attaching new files — "el mismo
// comportamiento que el botón de documentos de la fila del listado de hoy" (S209). Closing that
// dialog re-reads the block in the background (`refresh`, #3122): the block stays mounted, so the
// scroll and the open groups stay. `Document.jsx` gives no signal of a change, so every close re-reads.
//
// ── Rediseño del bloque (decisión del PO, lienzo `project/Documentos.dc.html`) ──────────────────
// 1. LOS GRUPOS VACÍOS DE SOLO LECTURA BAJAN A UNA LÍNEA. Tres barras a ancho completo repitiendo
//    "sin documentos en este grupo" se leían como tres párrafos y empujaban fuera de pantalla lo que
//    sí tiene documentos. ⚠️ PLEGAR NO ES OCULTAR (§2.3): la línea ENUMERA los grupos que pliega CON
//    SU RECUENTO ("Documentos de proceso 0 · Documentos de pedido 0"), así que sigue diciendo QUÉ
//    grupos existen y que están a cero —que es lo que S206/S210 protegían— sin abrir nada; y el
//    botón devuelve el grupo entero, con su mensaje, para quien lo quiera leer como antes. Solo se
//    pliega lo que está VACÍO: un grupo CON documentos no se pliega jamás.
// 2. EL GRUPO DE LA OF SE SEPARA Y SE MARCA. Es el único donde se puede adjuntar o retirar, y hasta
//    ahora eso solo se sabía leyendo una frase al principio del bloque ("...View only.") que además
//    mentía a medias. Ahora la capacidad vive donde se ejerce: el botón de adjuntar está DENTRO de
//    la cabecera de ese grupo (antes colgaba suelto al pie, lejos del grupo al que afecta) y los
//    demás grupos declaran "solo lectura". El realce verde MIC no es el único portador (§2.5): el texto
//    del botón y el rótulo "solo lectura" dicen lo mismo sin color.
const DocumentsBlock = ({ fabricationOrderId, fabricationOrderCode = '', documents = {}, onDocumentsChanged = () => {}, showAlert = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDate } = useDateFormatter()
  const isUserAuthorized = useAuthorization()
  const [viewerDocument, setViewerDocument] = useState(null)
  const [manageOpen, setManageOpen] = useState(false)
  const [documentToRemove, setDocumentToRemove] = useState({})
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)

  const canView = isUserAuthorized('DOCUMENTS_FABRICATION_ORDER_VIEW')
  const canDownload = isUserAuthorized('DOCUMENTS_FABRICATION_ORDER_DOWNLOAD')
  const canDelete = isUserAuthorized('DOCUMENTS_FABRICATION_ORDER_DELETE')

  const groups = documents.groups || []
  const attachableProvenance = attachableProvenanceOf(groups)
  const attachableGroups = groups.filter((group) => group.provenance === attachableProvenance)
  // Si el grupo adjuntable no viniera (contrato roto o respuesta a medias), la sección se pinta
  // vacía igualmente: quedarse sin ella dejaría la orden SIN forma de adjuntar, que es peor que
  // enseñar un grupo a cero.
  const attachableGroup = attachableGroups.length > 0 ? attachableGroups[0] : { provenance: attachableProvenance, documents: [] }
  const readOnlyGroups = groups.filter((group) => group.provenance !== attachableProvenance)
  const filledReadOnlyGroups = readOnlyGroups.filter((group) => documentsOf(group).length > 0)
  const emptyReadOnlyGroups = readOnlyGroups.filter((group) => documentsOf(group).length === 0)

  // Una procedencia sin rótulo conocido se pinta con su identificador: preferible a un hueco, y se
  // ve en pantalla el día que Sun añada un grupo (RN8).
  const groupTitle = (provenance) => (GROUP_TITLE_KEYS[provenance] ? t_(GROUP_TITLE_KEYS[provenance]) : provenance)

  const downloadDocument = async (document) => {
    if (!canDownload) {
      return
    }
    setIsDownloadingFile(true)
    try {
      await apiDownloadFile(`document/file/download/${document.uuid}`, document.name)
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error'), message: t_(error?.message || '') })
    } finally {
      setIsDownloadingFile(false)
    }
  }

  // S290/H3: the SAME existing delete route the row button of today already uses — no new
  // endpoint. Only offered where Sun says `attachable` (D10/RN45) AND the user has `_DELETE`.
  // Same flow as the legacy window's document table (SmartMateriaUITable): the row button only asks
  // ("Delete data?"), the DELETE runs on confirming, and success is announced ("Deleted correctly.").
  const requestRemoval = (document) => {
    if (!canDelete || !document.attachable) {
      return
    }
    setDocumentToRemove(document)
  }

  const removeDocument = async (document) => {
    setDocumentToRemove({})
    try {
      await apiDelete(`document/delete/FABRICATION_ORDER/${fabricationOrderId}/${document.documentId}`)
      showAlert({ title: t_('Deleted correctly.'), severity: 'success' })
      onDocumentsChanged()
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error'), message: t_(error?.message || '') })
    }
  }

  const closeManageDialog = () => {
    setManageOpen(false)
    onDocumentsChanged()
  }

  // Se pinta con una función y no con un componente declarado dentro del render: un componente nuevo
  // en cada render remonta su subárbol (y con él las tablas y el foco); una función solo devuelve
  // JSX.
  const renderGroupSection = (group, isAttachable) => {
    const rows = documentsOf(group)
    return (
      <Box
        key={group.provenance}
        component='section'
        data-testid={`documents-group-${group.provenance}`}
        sx={{
          borderRadius: 2,
          border: `1px solid ${isAttachable ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.text.primary, 0.08)}`,
          backgroundColor: raisedColor(theme),
          overflow: 'hidden'
        }}
      >
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{
            px: 2.5,
            py: 1,
            minHeight: 56,
            backgroundColor: raisedColor(theme),
            borderBottom: rows.length > 0 ? `1px solid ${alpha(theme.palette.text.primary, 0.08)}` : 'none'
          }}
        >
          <Typography component='h2' variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
            {groupTitle(group.provenance)}
          </Typography>
          {/* El recuento es una CIFRA, no una ausencia: un grupo a cero dice 0 y nunca "—" (§2.2). */}
          <Chip
            label={rows.length}
            size='small'
            sx={{
              height: 20,
              fontWeight: 800,
              fontFamily: 'monospace',
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              color: 'primary.main'
            }}
          />
          {rows.length === 0 && (
            <Typography data-testid={`documents-group-empty-${group.provenance}`} variant='caption' sx={{ color: 'text.secondary' }}>
              {/* El grupo de la OF vacío no dice lo mismo que uno ajeno vacío: en aquel el vacío es
                  una invitación ("ninguno adjunto todavía"), en este es un hecho cerrado. */}
              {isAttachable ? t_('None attached yet') : t_('No documents in this group')}
            </Typography>
          )}
          {!isAttachable && (
            <Typography data-testid={`documents-group-readonly-${group.provenance}`} variant='caption' sx={{ color: 'text.disabled' }}>
              {t_('Read only')}
            </Typography>
          )}
          {/* ⚠️⚠️ CONTORNO Y NO RELLENO, y desentonaba por DOS motivos distintos, no por gusto.
              · POR PESO: en toda la ficha `variant='contained'` está reservado a la acción que
                COMPROMETE algo —«Guardar cambios», «Cambiar estado», «Confirmar nueva fecha de
                entrega»—. Adjuntar no compromete nada: ABRE UN DIÁLOGO. Llevaba el peso visual más
                fuerte del cuadro para lo que solo es el primer paso de otra cosa.
              · POR COLOR: era el único azul relleno entre verdes. En esta ficha el azul lo usan
                cosas SELECCIONABLES o EN CURSO —la ficha de la pieza elegida, «En ejecución»—, no
                acciones.
              30/09: el PO decidió que el grupo de la OF (borde y botón) pasa del azul al verde MIC
              (`primary`, como el borde de la ficha elegida en PartsBlock), anulando lo pedido en
              #2895. Y `minHeight: 44` sigue puesto a mano (§2.5): es justo lo que se cae sin querer
              en un cambio «solo visual», y tiene test propio que lo mide con `getComputedStyle`. */}
          {isAttachable && (
            <Button
              data-testid='documents-manage-button'
              onClick={() => setManageOpen(true)}
              variant='outlined'
              color='primary'
              startIcon={<AddIcon />}
              sx={{ ml: 'auto', minHeight: 44, textTransform: 'none', fontWeight: 700, borderRadius: CONTROL_RADIUS }}
            >
              {t_('Attach document')}
            </Button>
          )}
        </Stack>

        {rows.length > 0 && (
          <TableContainer>
            <Table size='small' aria-label={groupTitle(group.provenance)}>
              <TableHead>
                <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                  <TableCell><ColumnHeader>{t_('Name')}</ColumnHeader></TableCell>
                  <TableCell><ColumnHeader>{t_('Type')}</ColumnHeader></TableCell>
                  <TableCell><ColumnHeader>{t_('Belongs to')}</ColumnHeader></TableCell>
                  <TableCell><ColumnHeader>{t_('Author')}</ColumnHeader></TableCell>
                  <TableCell><ColumnHeader>{t_('Date')}</ColumnHeader></TableCell>
                  <TableCell align='right'><ColumnHeader align='right'>{t_('Size')}</ColumnHeader></TableCell>
                  <TableCell align='right' />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((document) => (
                  <TableRow key={document.documentId} data-testid={`document-row-${document.documentId}`} hover>
                    <TableCell>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <DescriptionOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} fontSize='small' />
                        <Typography component='span' data-testid='document-name' variant='body2' sx={{ fontWeight: 700 }}>
                          {document.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        component='span'
                        data-testid='document-type'
                        size='small'
                        label={document.documentType || EMPTY_DASH}
                        sx={{ fontWeight: 700, backgroundColor: sunkenColor(theme) }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography component='span' data-testid='document-belongs-to' variant='body2' sx={{ color: 'text.secondary' }}>
                        {document.belongsTo?.label || EMPTY_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component='span' data-testid='document-created-user' variant='caption' sx={{ color: 'text.secondary' }}>
                        {document.createdUser || EMPTY_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component='span' data-testid='document-created-date' variant='caption' sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        {document.createdDate ? formatDate(document.createdDate) : EMPTY_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography component='span' data-testid='document-size' variant='caption' sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                        {documentSizeLabel(document.sizeBytes)}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      {/* §2.5: 44×44 de verdad, y no el `size='small'` de MUI (30 px), que en una
                          pantalla de planta y con guantes se falla más de lo que se acierta. */}
                      <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                        <IconButton
                          aria-label={t_('View document')}
                          onClick={() => setViewerDocument(document)}
                          disabled={!canView}
                          sx={{ width: 44, height: 44 }}
                        >
                          <Visibility fontSize='small' />
                        </IconButton>
                        <IconButton
                          aria-label={t_('Download document')}
                          onClick={() => downloadDocument(document)}
                          disabled={!canDownload || isDownloadingFile}
                          sx={{ width: 44, height: 44 }}
                        >
                          <FileDownloadOutlined fontSize='small' />
                        </IconButton>
                        {/* S211/A-W5.1: unlike ADD (offered disabled), retiring without
                            `_DELETE` is not offered at all — "no se ofrece retirar ningún
                            documento". */}
                        {document.attachable && canDelete && (
                          <IconButton
                            aria-label={t_('Remove document')}
                            onClick={() => requestRemoval(document)}
                            sx={{ width: 44, height: 44 }}
                          >
                            <DeleteOutline fontSize='small' />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    )
  }

  return (
    <Box data-testid='documents-block'>
      {/* La frase de arriba ya no carga con el "solo consulta": eso lo dice ahora cada grupo, que es
          donde se lee al mismo tiempo que se busca el botón. */}
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2.5 }}>
        {t_('Grouped by where they come from')}
      </Typography>

      <Stack spacing={2.5}>
        {filledReadOnlyGroups.map((group) => renderGroupSection(group, false))}
        {renderGroupSection(attachableGroup, true)}
      </Stack>

      {emptyReadOnlyGroups.length > 0 && (
        <CollapsedFieldsLine
          testId='documents-empty-groups'
          headline={t_('No documents')}
          openLabel={t_('See the empty groups')}
          // PLEGAR NO ES OCULTAR: cada grupo plegado se nombra CON SU RECUENTO. Quien no abra nada
          // sigue leyendo qué grupos hay y que están a cero.
          summary={emptyReadOnlyGroups.map((group) => `${groupTitle(group.provenance)} ${documentsOf(group).length}`)}
        >
          <Stack spacing={2.5}>
            {emptyReadOnlyGroups.map((group) => renderGroupSection(group, false))}
          </Stack>
        </CollapsedFieldsLine>
      )}

      {/* Los dos `renderComponent` de abajo van con ternario y `null`, no con `condición && (...)`:
          con `&&` la rama falsa no da `null`, da `false`, y el propType de `SmartDialog` es
          `oneOfType([element, string])`, así que React avisa en cada render. No se ve nada raro
          —React no pinta `false`—, pero es ruido que tapa avisos de verdad. Los demás llamantes del
          diálogo ya usan ternario (p. ej. `EditCalendarEventsComponent.jsx:300`). */}
      <SmartDialog
        setOpen={manageOpen}
        accept={false}
        cancel={false}
        close
        closeCallback={closeManageDialog}
        minWidth='60vw'
        paperHeight='75vh'
        renderComponent={manageOpen
          ? (
            <Suspense fallback={<DocumentDialogFallback />}>
              {/* Defecto visto por el PO: el diálogo se titulaba "Documentos - undefined".
                `Document.jsx:612` compone el título con `defaultName || reference?.name`, y aquí
                viajaba una referencia con `id` y nada más.
                Se pasa la referencia con nombre, y NO `defaultName`, porque es lo que hacen los diez
                llamantes del componente —todos le dan la fila— y en particular el OTRO llamante de
                FABRICATION_ORDER (`parts/Part.jsx:714`), que construye `{id, code, name}` con el
                código de la orden en las dos. Así las dos pantallas titulan igual.
                `Document` solo lee `reference.id` y `reference.name`: con estos dos no queda ningún
                otro hueco por el que se cuele un `undefined`. */}
              <Document
                reference={{ id: fabricationOrderId, code: fabricationOrderCode, name: fabricationOrderCode }}
                resourceType='FABRICATION_ORDER'
                isDownloadingFile={isDownloadingFile}
                setIsDownloadingFile={setIsDownloadingFile}
              />
            </Suspense>
            )
          : null}
        growContainerHeight
      />

      {documentToRemove.documentId !== undefined && (
        <SmartDeleteDialog
          show
          cancelCallback={() => setDocumentToRemove({})}
          deleteCallBack={() => removeDocument(documentToRemove)}
          rows={[documentToRemove]}
          columns={[{ name: t_('Document name'), field: 'name' }]}
        />
      )}

      <SmartDialog
        setOpen={!!viewerDocument}
        accept={false}
        cancel={false}
        close
        closeCallback={() => setViewerDocument(null)}
        minWidth='60vw'
        paperHeight='75vh'
        renderComponent={viewerDocument
          ? (
            <Suspense fallback={<DocumentDialogFallback />}>
              <SmartDocumentViewer
                documentUUID={viewerDocument.uuid}
                documentName={viewerDocument.name}
                sensitive={viewerDocument.sensitive === true}
                extension={viewerDocument.extension}
              />
            </Suspense>
            )
          : null}
        growContainerHeight
      />
    </Box>
  )
}

export default DocumentsBlock
